import { useNavigate } from "react-router-dom";
import { ArrowLeft, ScrollText } from "lucide-react";
import { PageHeader } from "@/components/perf";

const SECTIONS = [
  {
    title: "1. Nature of Platform Credits",
    body: [
      "Credits are digital units issued by Pickle Bullpen for use exclusively within the Platform.",
      "Credits:",
      ["Are not legal tender", "Are not currency", "Are not cryptocurrency", "Are not a bank account or stored monetary value",
       "Do not represent ownership, equity, or investment in Pickle Bullpen", "Do not earn interest",
       "Are not insured or guaranteed by any government entity"],
      "Credits are intended solely to facilitate access to services, products, and experiences within the Platform.",
    ],
  },
  {
    title: "2. Credit Types",
    body: [
      "The Platform may issue different categories of Credits, including:",
      "A. Purchased Credits — Credits obtained by paying U.S. dollars or other approved payment methods.",
      "B. Bonus Credits — Promotional credits issued for rewards, referrals, or incentives. Bonus Credits may have additional limitations including restricted redemption, expiration, or reduced conversion value.",
    ],
  },
  {
    title: "3. Use of Credits",
    body: [
      "Credits may be used for transactions within the Platform, including but not limited to:",
      ["Court bookings", "Coaching sessions", "Leagues and tournaments", "Merchandise purchases",
       "Food and beverage purchases", "Approved partner services"],
      "Credits have no use outside the Platform.",
    ],
  },
  {
    title: "4. Pricing and Value",
    body: [
      "Credits do not have a fixed or guaranteed monetary value.",
      "The Platform may:",
      ["Offer pricing discounts when Credits are used", "Adjust internal pricing at any time",
       "Modify the relative value of Credits within the ecosystem"],
      "Any displayed equivalency to U.S. dollars is for convenience only and may change.",
    ],
  },
  {
    title: "5. Redemption and Withdrawal",
    body: [
      "Credits may be eligible for redemption under limited conditions.",
      "Redemption is:",
      ["Not guaranteed", "Subject to eligibility requirements", "Subject to processing delays",
       "Subject to minimum withdrawal thresholds", "Subject to monthly or periodic limits",
       "Subject to applicable fees or conversion rates"],
      "The Platform reserves the right to approve, deny, delay, or limit any redemption request.",
    ],
  },
  {
    title: "6. Bonus Credit Limitations",
    body: [
      "Bonus Credits:",
      ["May not be redeemable for cash at full value", "May be subject to vesting schedules",
       "May have reduced redemption value", "May expire after a specified period",
       "May be restricted to certain uses"],
      "Bonus Credits are promotional and are not equivalent to Purchased Credits.",
    ],
  },
  {
    title: "7. Expiration and Inactivity",
    body: [
      "Credits may expire after a period of inactivity or as defined by the Platform.",
      "Expired Credits:",
      ["Are removed from user accounts", "Have no remaining value", "Are not refundable"],
    ],
  },
  {
    title: "8. Fees",
    body: [
      "The Platform may charge fees including:",
      ["Credit purchase fees", "Transaction fees", "Redemption fees", "Service fees"],
      "All fees are subject to change at any time.",
    ],
  },
  {
    title: "9. Account and Security",
    body: [
      "Users are responsible for:",
      ["Maintaining account security", "Protecting login credentials", "All activity conducted under their account"],
      "The Platform is not responsible for losses resulting from unauthorized account access.",
    ],
  },
  {
    title: "10. Fraud and Abuse",
    body: [
      "The following are strictly prohibited:",
      ["Attempting to convert Credits into cash through unauthorized means", "Creating artificial transactions",
       "Self-dealing between accounts to extract value", "Any misuse of the Platform"],
      "The Platform reserves the right to:",
      ["Suspend or terminate accounts", "Reverse transactions", "Forfeit Credits", "Deny redemption"],
    ],
  },
  {
    title: "11. Modification of Policy",
    body: [
      "The Platform reserves the right to modify this Policy at any time.",
      "Continued use of Credits after changes constitutes acceptance of the updated Policy.",
    ],
  },
  {
    title: "12. Limitation of Liability",
    body: [
      "To the maximum extent permitted by law, Pickle Bullpen shall not be liable for:",
      ["Loss of Credits", "Changes in Credit value", "Redemption limitations", "System interruptions"],
      "Credits are provided \u201cas-is\u201d without warranties of any kind.",
    ],
  },
];

export default function CreditPolicy() {
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl mx-auto" data-testid="credit-policy-page">
      <button data-testid="policy-back-btn" onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-pb-red transition-colors mb-6">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <PageHeader kicker="BULLION COIN · Platform Credits" title="Platform Credit Policy"
        subtitle="This Platform Credit Policy governs the use of all digital credits issued and used within the Pickle Bullpen ecosystem. By accessing or using Credits, you agree to this Policy in full." />
      <div className="flex items-center gap-2 text-xs text-zinc-500 mb-8" data-testid="policy-updated">
        <ScrollText className="w-4 h-4 text-pb-red" /> Last Updated: 7/14/2026
      </div>
      <div className="space-y-8 pb-12">
        {SECTIONS.map((s) => (
          <section key={s.title} className="bg-pb-surface border border-pb-border p-6" data-testid={`policy-section-${s.title.split(".")[0]}`}>
            <h2 className="font-display font-black uppercase text-lg text-white mb-3">{s.title}</h2>
            <div className="space-y-2.5 text-sm text-zinc-400 leading-relaxed">
              {s.body.map((b, i) =>
                Array.isArray(b) ? (
                  <ul key={i} className="list-none space-y-1.5 pl-1">
                    {b.map((li) => (
                      <li key={li} className="flex items-start gap-2">
                        <span className="text-pb-red mt-0.5 shrink-0">›</span> {li}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p key={i}>{b}</p>
                )
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
